/**
 * Centralized localStorage cleanup utility for Syncreos
 * Handles cleanup of user-specific data on logout and token invalidation
 */

export class LocalStorageCleanup {
  // Known storage keys that should be cleaned up
  private static readonly CLEANUP_KEYS = [
    // Zustand persist keys
    'syncreos-auth-storage',
    'syncreos-friends-storage',
    'feedback-storage',

    // Voice call related
    'syncreos-presence-state',
    'syncreos-call-preferences',

    // Project/user data
    'user-joined-projects',
    'lastQueueConversationId',

    // Agora/WebRTC SDK keys
    'websdk_ng_cache_parameter',
    'websdk_ng_global_parameter',
    'websdk_ng_install_id',
  ];

  // Patterns for keys that contain user/project specific data
  private static readonly CLEANUP_PATTERNS = [
    /^project-conversation-/, // project-conversation-{uuid}
    /^queue-conversation-/,   // queue-conversation-{projectId}
    /^workspace_\d+_/,        // workspace_{projectId}_{userId}
    /^contributions_\d+$/,    // contributions_{projectId}
    /^following_[^_]+$/,      // following_{userId}
    /^followers_[^_]+$/,      // followers_{userId}
    /^syncreos-/,             // Any keys created by the localStorage utility
  ];

  /**
   * Clean up all user-specific localStorage data
   */
  static cleanupAll(): void {
    console.log('LocalStorageCleanup: Starting cleanup of user data...');

    // Remove known static keys
    this.CLEANUP_KEYS.forEach(key => {
      try {
        localStorage.removeItem(key);
        console.log(`LocalStorageCleanup: Removed ${key}`);
      } catch (error) {
        console.warn(`LocalStorageCleanup: Failed to remove ${key}:`, error);
      }
    });

    // Remove keys matching patterns
    this.cleanupByPatterns();

    // Clear any remaining follow/following data (old implementation)
    this.cleanupFollowData();

    console.log('LocalStorageCleanup: Cleanup completed');
  }

  /**
   * Clean up keys that match specific patterns
   */
  private static cleanupByPatterns(): void {
    const keysToRemove: string[] = [];

    // Get all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      // Check if key matches any cleanup pattern
      const shouldRemove = this.CLEANUP_PATTERNS.some(pattern => pattern.test(key));
      if (shouldRemove) {
        keysToRemove.push(key);
      }
    }

    // Remove matching keys
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        console.log(`LocalStorageCleanup: Removed pattern-matched key ${key}`);
      } catch (error) {
        console.warn(`LocalStorageCleanup: Failed to remove ${key}:`, error);
      }
    });
  }

  /**
   * Clean up old follow/following localStorage data
   */
  private static cleanupFollowData(): void {
    const followKeysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      // Check for old and current follow/following keys that may still exist
      if (key.includes('-following-list') ||
          key.includes('-follower-count') ||
          key.includes('-follow-state') ||
          key.startsWith('following_') ||
          key.startsWith('followers_')) {
        followKeysToRemove.push(key);
      }
    }

    followKeysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        console.log(`LocalStorageCleanup: Removed follow-related key ${key}`);
      } catch (error) {
        console.warn(`LocalStorageCleanup: Failed to remove ${key}:`, error);
      }
    });
  }

  /**
   * Clean up sessionStorage as well (for shared files, etc.)
   */
  static cleanupSessionStorage(): void {
    try {
      // Clear shared files from session storage
      sessionStorage.removeItem('sharedFiles');
      console.log('LocalStorageCleanup: Cleared shared files from sessionStorage');
    } catch (error) {
      console.warn('LocalStorageCleanup: Failed to clear sessionStorage:', error);
    }
  }

  /**
   * Perform complete cleanup (localStorage + sessionStorage)
   */
  static cleanupEverything(): void {
    this.cleanupAll();
    this.cleanupSessionStorage();
  }
}
